import React, { ReactNode, useEffect, useState, useMemo, useRef } from 'react';
import { Table as AntTable, TableProps, Input, Button, Switch, Dropdown, Form, Card } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useList } from '../../Hooks/useList';
import { useCustomers } from '../../Hooks/useCustomers';
import { useProducts } from '../../Hooks/useProducts';
import { useSalesRepresentatives } from '../../Hooks/useSalesRepresentatives';
import Can from '../Can';
import { ActionButton } from '../ui';
import './style.scss';

type TableCustomProps = {
  tableQueryKey?: string;
  tableTitle?: string;
  total?: number;
  containerClassName?: string;
  onSearch?: (searchText: string) => any;
  onTableFilterChange?: (path: string, value: any) => void;
  tableFilter?: any;
  onClickNewButton?: any;
  buttonContent?: string | React.ReactNode;
  createPermission?: string;
  customHeaderContent?: React.ReactNode;
  additionalFilters?: ReactNode;
  hideFilters?: boolean;
  showDeleteSwitch?: boolean;
  showSearch?: boolean;
  hideSearch?: boolean;
  baseURL?: string;
  initialFilters?: any;
  sortBy?: [{ label: string; value: string }[], string, string];
  newButtonMenu?: any;
  exportColumns?: string[];
  showExport?: boolean;
  onRowClick?: (record: any) => void;
} & TableProps<any>;

const TOTAL_PER_PAGE = 20;

export const Table = ({
  tableQueryKey,
  tableTitle,
  total,
  onSearch,
  onClickNewButton,
  containerClassName,
  buttonContent,
  customHeaderContent,
  onTableFilterChange,
  tableFilter,
  additionalFilters,
  hideFilters = true,
  showSearch,
  hideSearch,
  showDeleteSwitch,
  baseURL,
  initialFilters,
  sortBy,
  newButtonMenu: _newButtonMenu,
  exportColumns: _exportColumns,
  showExport,
  createPermission,
  onRowClick,
  ...antTableProps
}: TableCustomProps) => {
  // Mobile detection hook
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkMobileView();
    window.addEventListener('resize', checkMobileView);

    return () => window.removeEventListener('resize', checkMobileView);
  }, []);

  // Add data-label mapping to columns for mobile responsive design
  const enhancedColumns = antTableProps.columns?.map((column: any) => {
    const dataIndex = column.dataIndex;

    // Create label mapping for common dataIndex patterns
    const labelMap: { [key: string]: string } = {
      id: 'ID',
      name: 'Name',
      phone_number: 'Phone',
      billing_address: 'Address',
      total_invoices_count: 'Orders',
      total_invoices_amount: 'Total Spent',
      closing_balance: 'Balance',
      overdue: 'Overdue',
      status: 'Status',
      created_at: 'Created',
    };

    const label = labelMap[dataIndex] || dataIndex || 'Field';

    return {
      ...column,
      'data-label': label,
    };
  });

  // Update antTableProps with enhanced columns
  const enhancedTableProps = {
    ...antTableProps,
    columns: enhancedColumns,
  };
  const [, setOpenFiltersModal] = useState<any>();
  const [outerFilters] = useState({});
  const [sortByOptions, defaultOption] = sortBy || [[], ''];
  const [exportLoading, setExportLoading] = useState(false);
  const prevOuterFiltersRef = useRef<any>(outerFilters);
  const prevTableFilterRef = useRef<any>(tableFilter);

  // Use custom hook for customers, products, and sales representatives, otherwise use generic useList
  const isCustomersTable = tableQueryKey === 'customers';
  const isProductsTable = tableQueryKey === 'products';
  const isSalesRepresentativesTable = tableQueryKey === 'salesRepresentatives';

  const {
    filters,
    setListFilters,
    data: listData,
    isLoading: listLoading,
  } = isCustomersTable
    ? useCustomers({
        baseURL,
        initialFilters: tableFilter || initialFilters,
        disableErrorHandling: true,
      }) // Use useCustomers for 'customers'
    : isProductsTable
      ? useProducts({
          baseURL,
          initialFilters: tableFilter || initialFilters,
          disableErrorHandling: true,
        }) // Use useProducts for 'products'
      : isSalesRepresentativesTable
        ? useSalesRepresentatives({
            baseURL,
            initialFilters: tableFilter || initialFilters,
            disableErrorHandling: true,
          }) // Use useSalesRepresentatives for 'salesRepresentatives'
        : useList(tableQueryKey, {
            baseURL,
            initialFilters: tableFilter || initialFilters,
            disableErrorHandling: true,
          }); // Use generic useList for others

  const filtersRef = useRef(filters);
  const setListFiltersRef = useRef(setListFilters);

  const listTotal =
    total ||
    (listData as any)?.data?.total ||
    (listData as any)?.data?.count ||
    (listData as any)?.count ||
    (listData as any)?.total ||
    0; // Fixed data access for Laravel pagination
  const listFilter = filters;
  const setTableFilters = onTableFilterChange || setListFilters;
  const { t } = useTranslation();

  // Update ref whenever filters changes
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    setListFiltersRef.current = setListFilters;
  }, [setListFilters]);

  // Memoize outerFilters to prevent unnecessary re-renders
  const outerFiltersString = useMemo(() => JSON.stringify(outerFilters || {}), [outerFilters]);

  // Memoize tableFilter to prevent unnecessary re-renders
  const tableFilterString = useMemo(() => JSON.stringify(tableFilter || {}), [tableFilter]);

  useEffect(() => {
    // Only update if outerFilters actually changed
    const outerFiltersChanged =
      JSON.stringify(prevOuterFiltersRef.current || {}) !== outerFiltersString;

    if (outerFiltersChanged) {
      // Use ref to get current filters without depending on filters.filters
      setListFiltersRef.current('filters', {
        ...filtersRef.current.filters,
        ...outerFilters,
      });
      prevOuterFiltersRef.current = outerFilters;
    }
  }, [outerFiltersString]);

  // Update filters when tableFilter prop changes
  useEffect(() => {
    // Only update if tableFilter actually changed
    const tableFilterChanged =
      JSON.stringify(prevTableFilterRef.current || {}) !== tableFilterString;

    if (tableFilterChanged && tableFilter && tableFilter.filters) {
      setListFiltersRef.current('filters', tableFilter.filters);
      prevTableFilterRef.current = tableFilter;
    }
  }, [tableFilterString, tableFilter]);

  const handleSearch = (value: string) => {
    setTableFilters('filters', {
      ...listFilter.filters,
      name: value, // Map search to 'name' filter for backend
    });
    setTableFilters('paging', {
      ...listFilter.paging,
      page: 1,
    });
    onSearch && onSearch(value);
  };

  const handleDeleteSwitch = (checked: boolean) => {
    setTableFilters('filters', {
      ...listFilter.filters,
      isDeleted: checked,
    });
  };

  return (
    <div
      className={`table-container ${containerClassName || ''}`}
      style={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}
    >
      {/* Table Header */}
      <div className="table-header">
        <div className="table-title-section">
          {tableTitle && <h2 className="table-title">{tableTitle}</h2>}
          {customHeaderContent}
        </div>

        <div className="table-actions">
          {/* Search */}
          {showSearch && !hideSearch && (
            <Input
              placeholder={t('table.search', 'Search...')}
              prefix={<SearchOutlined />}
              onChange={(e) => handleSearch(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          )}

          {/* Filters */}
          {!hideFilters && (
            <Button icon={<FilterOutlined />} onClick={() => setOpenFiltersModal(true)}>
              {t('table.filters', 'Filters')}
            </Button>
          )}

          {/* Sort */}
          {sortByOptions.length > 0 && (
            <Dropdown
              overlay={
                <div className="sort-dropdown">
                  {sortByOptions.map((option) => (
                    <div
                      key={option.value}
                      className={`sort-option ${defaultOption === option.value ? 'active' : ''}`}
                      onClick={() => {
                        setTableFilters('sorting', { [option.value]: 1 });
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              }
            >
              <Button icon={<SortAscendingOutlined />}>{t('table.sort', 'Sort')}</Button>
            </Dropdown>
          )}

          {/* Delete Switch */}
          {showDeleteSwitch && (
            <div className="delete-switch">
              <span>{t('table.showDeleted', 'Show Deleted')}</span>
              <Switch onChange={handleDeleteSwitch} />
            </div>
          )}

          {/* Export */}
          {showExport && (
            <Button
              icon={<DownloadOutlined />}
              loading={exportLoading}
              onClick={() => {
                setExportLoading(true);
                // TODO: Implement export functionality
                setTimeout(() => setExportLoading(false), 1000);
              }}
            >
              {t('table.export', 'Export')}
            </Button>
          )}

          {/* New Button */}
          {onClickNewButton && (
            <Can permission={createPermission} fallback={null}>
              <ActionButton type="primary" onClick={onClickNewButton} style={{ marginLeft: 8 }}>
                {buttonContent || t('table.addNew', 'Add New')}
              </ActionButton>
            </Can>
          )}
        </div>
      </div>

      {/* Additional Filters */}
      {additionalFilters && (
        <div className="additional-filters">
          <Form layout="inline">{additionalFilters}</Form>
        </div>
      )}

      {/* Mobile Card View or Desktop Table */}
      {isMobileView ? (
        (() => {
          const mobileData = (() => {
            const d =
              (listData as any)?.data?.data ||
              (listData as any)?.data ||
              (listData as any)?.items ||
              [];
            return Array.isArray(d) ? d : [];
          })();
          const currentPage = filters?.paging?.page || 1;
          const pageSize = filters?.paging?.pageSize || TOTAL_PER_PAGE;
          const totalPages = Math.max(1, Math.ceil((listTotal || 0) / pageSize));

          return (
            <div className="mobile-table-list">
              {listLoading ? (
                <div className="mobile-loading">
                  <span>{t('table.loading', 'Loading...')}</span>
                </div>
              ) : mobileData.length === 0 ? (
                <div className="mobile-empty">
                  <span>{t('table.noData', 'No data')}</span>
                </div>
              ) : (
                mobileData.map((item: any, rowIndex: number) => {
                  const actionsColumn = enhancedTableProps.columns?.find(
                    (col: any) => col.key === 'actions'
                  );

                  return (
                    <Card
                      key={item.id || rowIndex}
                      className={`mobile-card${onRowClick ? ' mobile-card-clickable' : ''}`}
                      onClick={() => onRowClick?.(item)}
                    >
                      {enhancedTableProps.columns?.map((column: any, colIndex: number) => {
                        if (column.key === 'actions') return null;

                        const dataIndex = column.dataIndex;
                        const rawValue = dataIndex ? item[dataIndex] : undefined;

                        let label: React.ReactNode = dataIndex || '';
                        if (column.title) {
                          if (typeof column.title === 'string') {
                            label = column.title;
                          } else if (typeof column.title === 'function') {
                            try {
                              label = column.title();
                            } catch {
                              label = (column as any)?.['data-label'] || dataIndex || '';
                            }
                          } else if (React.isValidElement(column.title)) {
                            label = column.title;
                          } else if (column.title?.props?.children) {
                            label = column.title.props.children;
                          }
                        }
                        if (!label) {
                          label = (column as any)?.['data-label'] || '';
                        }

                        let displayValue: React.ReactNode;
                        if (column.render) {
                          displayValue = column.render(rawValue, item, colIndex);
                        } else if (rawValue !== undefined && rawValue !== null) {
                          displayValue =
                            typeof rawValue === 'object' ? JSON.stringify(rawValue) : rawValue;
                        } else {
                          displayValue = '-';
                        }

                        return (
                          <div key={dataIndex || `col-${colIndex}`} className="mobile-card-field">
                            <span className="mobile-card-label">{label}:</span>
                            <span className="mobile-card-value">{displayValue}</span>
                          </div>
                        );
                      })}

                      {actionsColumn?.render && (
                        <div
                          className="mobile-card-actions"
                          role="toolbar"
                          onClick={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                          }}
                        >
                          {actionsColumn.render(null, item, rowIndex)}
                        </div>
                      )}
                    </Card>
                  );
                })
              )}

              {/* Mobile Pagination */}
              {listTotal > 0 && (
                <div className="mobile-pagination">
                  <Button
                    disabled={currentPage <= 1}
                    onClick={() =>
                      setListFilters('paging', {
                        page: currentPage - 1,
                        pageSize,
                      })
                    }
                  >
                    {t('table.previous', 'Previous')}
                  </Button>
                  <span className="mobile-pagination-info">
                    {currentPage} / {totalPages}
                  </span>
                  <Button
                    disabled={currentPage >= totalPages}
                    onClick={() =>
                      setListFilters('paging', {
                        page: currentPage + 1,
                        pageSize,
                      })
                    }
                  >
                    {t('table.next', 'Next')}
                  </Button>
                </div>
              )}
            </div>
          );
        })()
      ) : (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <AntTable
            pagination={{
              total: listTotal || 0,
              showTotal: (total, range) =>
                total > 0
                  ? `${range[0]}-${range[1]} ${t('table.of')} ${total} ${t('table.entries')}`
                  : `0 ${t('table.entries')}`,
              pageSize: filters?.paging?.pageSize || TOTAL_PER_PAGE,
              current: filters?.paging?.page || 1,
              pageSizeOptions: [10, 20, 50],
              showSizeChanger: true,
              showQuickJumper: true,
              hideOnSinglePage: false,
              onChange: (page, pageSize) => {
                setListFilters('paging', { page, pageSize });
              },
              onShowSizeChange: (current, size) => {
                setListFilters('paging', { page: 1, pageSize: size });
              },
            }}
            onChange={(paging, _, sorter) => {
              const sorting = sorter as any;
              const { field, order } = sorting;
              setTableFilters &&
                setTableFilters(
                  'sorting',
                  order ? { [field as string]: order === 'ascend' ? 1 : -1 } : {}
                );
            }}
            rowKey="id"
            dataSource={(() => {
              const data =
                (listData as any)?.data?.data ||
                (listData as any)?.data ||
                (listData as any)?.items ||
                [];
              // Ensure data is always an array and don't render empty table while loading
              return Array.isArray(data) ? data : [];
            })()}
            scroll={{
              y: antTableProps.scroll?.y || 600,
              x: antTableProps.scroll?.x || '100%',
            }}
            {...enhancedTableProps}
            loading={listLoading || enhancedTableProps.loading}
            style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
          />
        </div>
      )}
    </div>
  );
};
